/* eslint-disable react/function-component-definition */
import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Button, ProgressBar, Alert, Badge, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import {
  REACT_APP_API_CAFE_IMPORT_VALIDATE,
  REACT_APP_API_CAFE_IMPORT,
} from "../../redux/apiConstants";
import styles from "./CafeImportModal.module.scss";

const STEPS = {
  UPLOAD: 1,
  VALIDATE: 2,
  IMPORT: 3,
};

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = {
  "text/csv": ".csv",
  "application/json": ".json",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
};

const CafeImportModal = ({ show, handleClose, onSuccess }) => {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const fileInputRef = useRef(null);
  const token = getCookies("Token");

  const resetState = () => {
    setStep(STEPS.UPLOAD);
    setFile(null);
    setValidationResult(null);
    setImportResult(null);
    setLoading(false);
  };

  const handleCloseModal = () => {
    resetState();
    handleClose();
  };

  const validateFileType = (fileToCheck) => {
    const extension = fileToCheck.name.split(".").pop().toLowerCase();
    return ["csv", "json", "xlsx"].includes(extension);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (selectedFile) => {
    if (!validateFileType(selectedFile)) {
      toast.error("Invalid file type. Please upload CSV, JSON, or XLSX file.");
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 15MB limit.");
      return;
    }

    setFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleValidate = async () => {
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(REACT_APP_API_CAFE_IMPORT_VALIDATE, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // API returns { success, code, context, data } - access 'data' not 'result'
      const result = response.data.data || response.data.result;
      setValidationResult(result);
      setStep(STEPS.VALIDATE);
    } catch (error) {
      const message = error.response?.data?.message || "Validation failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file || !validationResult?.isValid) return;

    setLoading(true);
    setStep(STEPS.IMPORT);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(REACT_APP_API_CAFE_IMPORT, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // API returns { success, code, context, data } - access 'data' not 'result'
      const result = response.data.data || response.data.result;
      setImportResult({ success: true, count: result.imported });
      toast.success(`Successfully imported ${result.imported} cafes!`);
      if (onSuccess) onSuccess();
    } catch (error) {
      const message = error.response?.data?.message || "Import failed. Please try again.";
      setImportResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (format) => {
    const templates = {
      csv: {
        content: `name,address,latitude,longitude,description,hours,rating,image_url,city,country
"Sunrise Coffee","100 Market St, San Francisco, CA 94102",37.7937,-122.3965,"Cozy corner cafe with artisan roasts","Monday: 7:00 AM – 5:00 PM | Tuesday: 7:00 AM – 5:00 PM | Wednesday: 7:00 AM – 5:00 PM | Thursday: 7:00 AM – 5:00 PM | Friday: 7:00 AM – 5:00 PM | Saturday: 8:00 AM – 3:00 PM | Sunday: Closed",4.7,"https://example.com/cafe1.jpg","San Francisco","United States"
"Urban Grind","250 5th Ave, New York, NY 10001",40.7448,-73.9867,"Modern espresso bar downtown","Monday: 6:30 AM – 8:00 PM | Tuesday: 6:30 AM – 8:00 PM | Wednesday: 6:30 AM – 8:00 PM | Thursday: 6:30 AM – 8:00 PM | Friday: 6:30 AM – 8:00 PM | Saturday: 7:00 AM – 6:00 PM | Sunday: 8:00 AM – 4:00 PM",4.5,"https://example.com/cafe2.jpg","New York","United States"
"Mountain Brew","1500 Pearl St, Boulder, CO 80302",40.0189,-105.2747,"Mountain-inspired cafe with local beans","Monday: 6:00 AM – 5:00 PM | Tuesday: 6:00 AM – 5:00 PM | Wednesday: 6:00 AM – 5:00 PM | Thursday: 6:00 AM – 5:00 PM | Friday: 6:00 AM – 5:00 PM | Saturday: 7:00 AM – 4:00 PM | Sunday: Closed",4.8,"https://example.com/cafe3.jpg","Boulder","United States"`,
        type: "text/csv",
        filename: "cafe-import-sample.csv",
      },
      json: {
        content: JSON.stringify([
          {
            name: "Sunrise Coffee",
            address: "100 Market St, San Francisco, CA 94102",
            latitude: 37.7937,
            longitude: -122.3965,
            description: "Cozy corner cafe with artisan roasts",
            hours: "Monday: 7:00 AM – 5:00 PM | Tuesday: 7:00 AM – 5:00 PM | Wednesday: 7:00 AM – 5:00 PM | Thursday: 7:00 AM – 5:00 PM | Friday: 7:00 AM – 5:00 PM | Saturday: 8:00 AM – 3:00 PM | Sunday: Closed",
            rating: 4.7,
            image_url: "https://example.com/cafe1.jpg",
            city: "San Francisco",
            country: "United States"
          },
          {
            name: "Urban Grind",
            address: "250 5th Ave, New York, NY 10001",
            latitude: 40.7448,
            longitude: -73.9867,
            description: "Modern espresso bar downtown",
            hours: "Monday: 6:30 AM – 8:00 PM | Tuesday: 6:30 AM – 8:00 PM | Wednesday: 6:30 AM – 8:00 PM | Thursday: 6:30 AM – 8:00 PM | Friday: 6:30 AM – 8:00 PM | Saturday: 7:00 AM – 6:00 PM | Sunday: 8:00 AM – 4:00 PM",
            rating: 4.5,
            image_url: "https://example.com/cafe2.jpg",
            city: "New York",
            country: "United States"
          },
          {
            name: "Mountain Brew",
            address: "1500 Pearl St, Boulder, CO 80302",
            latitude: 40.0189,
            longitude: -105.2747,
            description: "Mountain-inspired cafe with local beans",
            hours: "Monday: 6:00 AM – 5:00 PM | Tuesday: 6:00 AM – 5:00 PM | Wednesday: 6:00 AM – 5:00 PM | Thursday: 6:00 AM – 5:00 PM | Friday: 6:00 AM – 5:00 PM | Saturday: 7:00 AM – 4:00 PM | Sunday: Closed",
            rating: 4.8,
            image_url: "https://example.com/cafe3.jpg",
            city: "Boulder",
            country: "United States"
          }
        ], null, 2),
        type: "application/json",
        filename: "cafe-import-sample.json",
      }
    };

    // XLSX uses static file from public folder
    if (format === "xlsx") {
      const a = document.createElement("a");
      a.href = "/cafe-import-sample.xlsx";
      a.download = "cafe-import-sample.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    const template = templates[format];
    if (!template) return;

    const blob = new Blob([template.content], { type: template.type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = template.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      <div className={`${styles.step} ${step >= STEPS.UPLOAD ? styles.active : ""} ${step > STEPS.UPLOAD ? styles.completed : ""}`}>
        <div className={styles.stepNumber}>1</div>
        <span>Upload</span>
      </div>
      <div className={styles.stepLine} />
      <div className={`${styles.step} ${step >= STEPS.VALIDATE ? styles.active : ""} ${step > STEPS.VALIDATE ? styles.completed : ""}`}>
        <div className={styles.stepNumber}>2</div>
        <span>Validate</span>
      </div>
      <div className={styles.stepLine} />
      <div className={`${styles.step} ${step >= STEPS.IMPORT ? styles.active : ""}`}>
        <div className={styles.stepNumber}>3</div>
        <span>Import</span>
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className={styles.uploadSection}>
      <div
        className={`${styles.dropZone} ${dragActive ? styles.dragActive : ""} ${file ? styles.hasFile : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.json,.xlsx"
          onChange={handleFileInputChange}
          className={styles.fileInput}
        />
        {file ? (
          <div className={styles.fileInfo}>
            <i className={`fa fa-file-alt ${styles.fileIcon}`} />
            <div className={styles.fileName}>{file.name}</div>
            <div className={styles.fileSize}>
              {(file.size / 1024).toFixed(1)} KB
            </div>
            <button
              type="button"
              className={styles.removeFile}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        ) : (
          <>
            <div className={styles.uploadIcon}>
              <i className="fa fa-cloud-upload-alt" />
            </div>
            <p className={styles.dropText}>
              Drag and drop your file here
              <span>or click to browse</span>
            </p>
            <div className={styles.acceptedFormats}>
              <Badge bg="secondary" className={styles.formatBadge}>CSV</Badge>
              <Badge bg="secondary" className={styles.formatBadge}>JSON</Badge>
              <Badge bg="secondary" className={styles.formatBadge}>XLSX</Badge>
            </div>
            <p className={styles.limits}>Max size: 15MB • Max cafes: 1000</p>
          </>
        )}
      </div>

      <div className={styles.templatesSection}>
        <h6 className={styles.templatesTitle}>
          <i className="fa fa-download" /> Download Sample Templates
        </h6>
        <div className={styles.templateButtons}>
          <button
            type="button"
            className={styles.templateBtn}
            onClick={() => downloadTemplate("csv")}
          >
            <i className="fa fa-file-csv" />
            <span>CSV Template</span>
          </button>
          <button
            type="button"
            className={styles.templateBtn}
            onClick={() => downloadTemplate("json")}
          >
            <i className="fa fa-file-code" />
            <span>JSON Template</span>
          </button>
          <button
            type="button"
            className={styles.templateBtn}
            onClick={() => downloadTemplate("xlsx")}
          >
            <i className="fa fa-file-excel" />
            <span>Excel Template</span>
          </button>
        </div>
        <p className={styles.templateNote}>
          Each template includes 3 example cafes with valid data
        </p>
      </div>
    </div>
  );

  const renderValidationStep = () => (
    <div className={styles.validationSection}>
      {validationResult?.isValid ? (
        <div className={styles.successBox}>
          <div className={styles.successIcon}>
            <i className="fa fa-check-circle" />
          </div>
          <h5>Validation Passed</h5>
          <p>All {validationResult.totalRows} cafes are valid and ready to import.</p>
          
          <div className={styles.previewTable}>
            <h6>Preview ({validationResult.totalRows} records)</h6>
            <div className={styles.tableWrapper}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Address</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResult.preview?.map((cafe, index) => (
                    <tr key={index}>
                      <td>{cafe.row}</td>
                      <td>{cafe.name}</td>
                      <td>{cafe.address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.errorBox}>
          <div className={styles.errorHeader}>
            <div className={styles.errorIcon}>
              <i className="fa fa-times-circle" />
            </div>
            <div>
              <h5>Validation Failed</h5>
              <p>
                Found {validationResult?.errorCount} errors in your file. 
                Please fix ALL errors and re-upload.
              </p>
            </div>
          </div>
          
          <div className={styles.errorList}>
            {validationResult?.errors?.map((error, index) => (
              <div key={index} className={styles.errorItem}>
                <div className={styles.errorRow}>Row {error.row}</div>
                <div className={styles.errorField}>{error.field}</div>
                <div className={styles.errorMessage}>{error.message}</div>
              </div>
            ))}
          </div>
          
          <Alert variant="warning" className={styles.warningNote}>
            <i className="fa fa-exclamation-triangle" /> No data will be imported until all rows are valid.
          </Alert>
        </div>
      )}
    </div>
  );

  const renderImportStep = () => (
    <div className={styles.importSection}>
      {loading ? (
        <div className={styles.loadingBox}>
          <Spinner animation="border" className={styles.spinner} />
          <h5>Importing Cafes...</h5>
          <p>Please wait, do not close this window.</p>
          <ProgressBar animated now={100} className={styles.progressBar} />
        </div>
      ) : importResult?.success ? (
        <div className={styles.successBox}>
          <div className={styles.successIcon}>
            <i className="fa fa-check-circle" />
          </div>
          <h5>Import Successful!</h5>
          <p>Successfully imported {importResult.count} cafes.</p>
          <p className={styles.statusNote}>
            All cafes have been added with status "Unclaimed"
          </p>
        </div>
      ) : (
        <div className={styles.errorBox}>
          <div className={styles.errorIcon}>
            <i className="fa fa-times-circle" />
          </div>
          <h5>Import Failed</h5>
          <p>{importResult?.error || "An error occurred during import."}</p>
          <Alert variant="info">
            No cafes were added to the database. Please try again.
          </Alert>
        </div>
      )}
    </div>
  );

  const renderGuidelines = () => (
    <div className={styles.guidelinesOverlay} onClick={() => setShowGuidelines(false)}>
      <div className={styles.guidelinesPanel} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className={styles.closeGuidelines}
          onClick={() => setShowGuidelines(false)}
        >
          <i className="fa fa-times" />
        </button>
        
        <h4 className={styles.guidelinesTitle}>
          <i className="fa fa-info-circle" /> Import Guidelines
        </h4>

        <div className={styles.guidelinesContent}>
          <section>
            <h6><i className="fa fa-asterisk" /> Required Fields</h6>
            <ul>
              <li><strong>name</strong> - Cafe name (2-200 characters)</li>
              <li><strong>address</strong> - Full street address</li>
              <li><strong>latitude</strong> - GPS latitude (-90 to 90)</li>
              <li><strong>longitude</strong> - GPS longitude (-180 to 180)</li>
            </ul>
          </section>

          <section>
            <h6><i className="fa fa-plus-circle" /> Optional Fields</h6>
            <ul>
              <li><strong>description</strong> - Cafe description (max 2000 chars)</li>
              <li><strong>hours</strong> - Operating hours (see format below)</li>
              <li><strong>rating</strong> - Rating 0-5 (e.g., 4.5)</li>
              <li><strong>image_url</strong> - URL to cafe image</li>
              <li><strong>city</strong> - City name</li>
              <li><strong>country</strong> - Country name</li>
            </ul>
          </section>

          <section>
            <h6><i className="fa fa-clock" /> Hours Format</h6>
            <p>Use pipe (|) to separate days:</p>
            <code>Monday: 7:00 AM – 5:00 PM | Tuesday: 7:00 AM – 5:00 PM | ...</code>
            <p>For closed days:</p>
            <code>Sunday: Closed</code>
          </section>

          <section className={styles.importantNotes}>
            <h6><i className="fa fa-exclamation-triangle" /> Important Notes</h6>
            <ul>
              <li>Maximum 100 cafes per import</li>
              <li>Duplicate cafes (same name within 100m) will cause import to fail</li>
              <li>All imported cafes will have status "Unclaimed"</li>
              <li>ZipCode is auto-derived from coordinates</li>
              <li>If hours format is invalid, it will be skipped (not an error)</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      size="lg"
      centered
      className={styles.importModal}
      backdrop="static"
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <i className="fa fa-file-import" /> Import Cafes
        </Modal.Title>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.infoBtn}
            onClick={() => setShowGuidelines(true)}
            title="Import Guidelines"
          >
            <i className="fa fa-info-circle" />
          </button>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCloseModal}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {renderStepIndicator()}
        
        {step === STEPS.UPLOAD && renderUploadStep()}
        {step === STEPS.VALIDATE && renderValidationStep()}
        {step === STEPS.IMPORT && renderImportStep()}
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        {step === STEPS.UPLOAD && (
          <>
            <Button variant="outline-secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleValidate}
              disabled={!file || loading}
              className={styles.primaryBtn}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" /> Validating...
                </>
              ) : (
                <>Continue <i className="fa fa-arrow-right" /></>
              )}
            </Button>
          </>
        )}

        {step === STEPS.VALIDATE && (
          <>
            <Button variant="outline-secondary" onClick={() => setStep(STEPS.UPLOAD)}>
              <i className="fa fa-arrow-left" /> Back
            </Button>
            {validationResult?.isValid ? (
              <Button
                variant="success"
                onClick={handleImport}
                className={styles.successBtn}
              >
                <i className="fa fa-check" /> Import All {validationResult.totalRows} Cafes
              </Button>
            ) : (
              <Button variant="secondary" disabled>
                <i className="fa fa-ban" /> Cannot Import
              </Button>
            )}
          </>
        )}

        {step === STEPS.IMPORT && !loading && (
          <>
            {importResult?.success ? (
              <>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    handleCloseModal();
                    window.location.reload();
                  }}
                >
                  View Cafes
                </Button>
                <Button variant="primary" onClick={handleCloseModal} className={styles.primaryBtn}>
                  Done
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline-secondary" onClick={() => setStep(STEPS.UPLOAD)}>
                  Try Again
                </Button>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
              </>
            )}
          </>
        )}
      </Modal.Footer>

      {showGuidelines && renderGuidelines()}
    </Modal>
  );
};

CafeImportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

CafeImportModal.defaultProps = {
  onSuccess: null,
};

export default CafeImportModal;

